// refactoring work in progress

import { Add, Refresh } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Icon, Link, MenuItem, Paper, Switch, Tab, Tabs, Typography, styled } from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import moment from "moment";
import * as React from "react";
import { AiFillCloseCircle } from "react-icons/ai";
import { BiTrash } from "react-icons/bi";
import { FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as Yup from 'yup';
import { VdsPageTitle, VdsSelect, VdsTextField } from "../../@vds";
import { RT, VdsTable } from "../../@vds/core/VdsTable/VdsTable";
import { useAlertContext, useLoaderContext, useProfileContext } from "../../contexts";
import { useDialogContextWithError } from "../../contexts/DialogContextWithError";
import { ActivateUserByRole, AppInCloudRoles, DeleteActivationUser, GetActivationUsers, GetActivationUsersResult, GetLicenses, GetLicensesResult, GetUserRoles, GetUserRolesResult, UpdateUserByRole } from "../../helpers/api.helpers";
import { useLocationState } from "../../utilities/useLocationState";
import styles from './ActivationDetailsPage.module.css';
import { LicenseManager } from "./components/LicenseManager";
import { GetActivation } from "../../helpers/activations.services";

export const renderBooleanIcon = (enabled: boolean) => (
    <Icon sx={{ display: 'flex', alignItems: 'center', margin: 'auto', justifyContent: 'center' }}>
        {enabled ?
            <FaCheckCircle style={{ color: "#09822A", fontSize: '16px' }} /> :
            <AiFillCloseCircle style={{ color: "grey", fontSize: '18px' }} />}
    </Icon>
);

const rolesTslMap: Record<AppInCloudRoles, string> = {
    CompanyAdmin: 'Amministratore',
    Operator: 'Operatore'
}

enum ActivationDetailsTab {
    Licenses = 0,
    Users = 1,
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    hidden?: boolean;
    onEnter?: () => void;
}

export function TabPanel(props: TabPanelProps) {
    const { children, value, index, hidden, onEnter, ...other } = props;

    React.useEffect(() => {

        if (!hidden && value === index && onEnter) {

            onEnter();
        }

    }, [hidden, value]);

    if (hidden) return null;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const StyledPageContainer = styled('div')(() => ({
    backgroundColor: "var(--vapor-primary-background)",
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
}));

export const useActivationDetailsView = () => {

    const setLoader = useLoaderContext();
    const navigate = useNavigate();

    return (props: { activationId: string; companyName: string; companyOid: string; }) => {

        setLoader(true);
        GetActivation({
            params: {
                id: props.activationId
            }
        }).then((activationResult) => {
            GetUserRoles().then((roleResult) => {

                GetLicenses({
                    params: {
                        companyOid: props.companyOid
                    }
                }).then((licenseResult) => {
                    navigate("/app/companies/licenses", {
                        state: {
                            license: licenseResult.data,
                            activation: {
                                ...activationResult.data,
                                id: props.activationId
                            },
                            company: {
                                name: props.companyName,
                                id: props.companyOid
                            },
                            roles: roleResult.data?.roles || []
                        }
                    })
                }).catch((result) => {

                    // caso licenza not found
                    if (result.response.data.title === '404.1007') {

                        navigate("/app/companies/licenses", {
                            state: {
                                license: null,
                                activation: {
                                    ...activationResult.data,
                                    id: props.activationId
                                },
                                company: {
                                    name: props.companyName,
                                    id: props.companyOid
                                },
                                roles: roleResult.data?.roles || []
                            }
                        })
                    }
                }).finally(() => {
                    setLoader(false);
                });
            }).finally(() => {
                setLoader(false);
            });
        }).catch(() => {
            setLoader(false);
        });
    }
}

interface ActivationDetailsPageLocationState {
    license: {
        oid: string
        companyOid: string
        code: string
        type: string
        description: string
        slots: number
        validityStartDate: string
        validityEndDate: string
        creationDate: string
        lastUpdateDate: string
        enabled: boolean
    } | null;
    activation: {
        id: string
        url: string
        serverOid: string
        serverUri: string
        serverDomain: string
        licenseOid: string
        licenseSlots: number
        companyUsers: number
        roleOid: string
        roleName: string
    },
    roles: {
        oid: string
        name: AppInCloudRoles
    }[],
    company: {
        id: string
        name: string
    }
}

export const ActivationDetailsPage = () => {

    const navigate = useNavigate();
    const {
        company,
        roles = [],
        ...location
    } = useLocationState<ActivationDetailsPageLocationState>();

    const usersTableRef = React.useRef(null) as React.RefObject<RT<GetActivationUsersResult['users'][0]>>;
    const setAlert = useAlertContext();
    const setLoader = useLoaderContext();
    const setDialog = useDialogContextWithError();
    const [profile] = useProfileContext();

    const [panel, setPanel] = React.useState<number>(0);
    const [userDialog, setUserDialog] = React.useState(false);

    const [activation, setActivation] = React.useState(location.activation);
    const [license, setLicense] = React.useState(location.license);

    const navigateToCompaniesPage = () => {
        navigate('/app');
    }

    const handlePanelChange = (_: unknown, newPanel: number) => {

        if (newPanel === ActivationDetailsTab.Licenses) {

            reloadActivationAndLicenseData(() => {

                setPanel(newPanel);
            });
        } else {

            setPanel(newPanel);
        }
    }

    const formik = useFormik({
        initialValues: {
            email: '',
            roleOid: '',
            activationOid: '',
            edit: false,
            enabled: true
        },
        validationSchema: Yup.object().shape({
            email: Yup
                .string()
                .email("Inserire un indirizzo E-mail valido")
                .required("Inserire un indirizzo E-mail valido"),
            roleOid: Yup
                .string()
                .required("Selezionare un ruolo valido"),
        }),
        onSubmit: (values) => {

            setLoader(true);
            if (values.edit) {

                UpdateUserByRole({
                    params: { activationOid: values.activationOid },
                    data: {
                        roleId: values.roleOid,
                        enabled: values.enabled
                    }
                }).then(() => {
                    setLoader(false);
                    setUserDialog(false);
                    usersTableRef.current?.reload();
                    setAlert({
                        open: true,
                        severity: 'success',
                        message: "Utente modificato con successo"
                    });
                }).catch((error) => {
                    setLoader(false);
                    const errors = {
                        '403.3004': 'Non ci sono slot disponibili'
                    }
                    setAlert({
                        message: errors[error?.response?.data?.title] || 'Si è verificato un errore imprevisto si prega di riprovare',
                        severity: 'warning',
                        open: true,
                    });
                });
            } else {
                ActivateUserByRole({
                    params: { companyOid: company.id },
                    data: {
                        email: values.email,
                        roleOid: values.roleOid
                    }
                }).then(() => {
                    setLoader(false);
                    setUserDialog(false);
                    usersTableRef.current?.reload();
                    setAlert({
                        open: true,
                        severity: 'success',
                        message: "Utente aggiunto con successo"
                    });
                }).catch((error) => {
                    setLoader(false);
                    const errors = {
                        '403.3004': 'Non ci sono slot disponibili sulla licenza',
                        '409.3004': 'Non ci sono slot disponibili sulla licenza',
                        '409.2003': 'Esiste già un utente con lo stesso indirizzo Email'
                    }
                    setAlert({
                        message: errors[error?.response?.data?.title] || 'Si è verificato un errore imprevisto si prega di riprovare',
                        severity: 'warning',
                        open: true,
                    });
                });
            }
        }
    });

    const reloadActivationAndLicenseData = (callback?: () => void) => {

        setLoader(true);
        GetActivation({
            params: {
                id: activation.id
            }
        }).then((activationResult) => {

            GetLicenses({
                params: {
                    companyOid: company.id
                }
            }).then((licenseResult) => {

                setActivation({
                    ...activationResult.data,
                    id: activation.id
                });
                setLicense(licenseResult.data);
                callback?.();
            }).finally(() => {
                setLoader(false);
            });
        }).catch(() => {
            setLoader(false);
        });
    }

    const dateNow = dayjs();
    const isActive = license?.validityEndDate && dayjs(license.validityEndDate).isAfter(dateNow) && dayjs(license.validityStartDate).isBefore(dateNow);

    return (
        <StyledPageContainer>

            <VdsPageTitle
                title={`Azienda '${company.name}'`}
                historyBackEvent={navigateToCompaniesPage}
                content={
                    <Breadcrumbs sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link onClick={navigateToCompaniesPage}>Home</Link>
                        <Typography>Dettagli</Typography>
                    </Breadcrumbs>
                }
            />

            <Container
                component="form"
                noValidate
                autoComplete="off"
                spellCheck="false"
                maxWidth="xl"
                style={{ height: 'calc(100% - 116px)', overflow: 'auto' }}
            >
                <Paper variant="outlined" className={styles.paperContainer}>
                    <Box className={styles.tabsWrapper}>
                        <Tabs
                            value={panel}
                            onChange={handlePanelChange}
                        >
                            <Tab
                                label="Licenze"
                                value={ActivationDetailsTab.Licenses}
                            />
                            {license && (
                                <Tab
                                    label="Utenti"
                                    value={ActivationDetailsTab.Users}
                                />
                            )}
                        </Tabs>
                    </Box>

                    <TabPanel
                        value={panel}
                        hidden={panel !== ActivationDetailsTab.Licenses}
                        index={ActivationDetailsTab.Licenses}
                    >
                        <LicenseManager
                            license={license}
                            activation={activation}
                            company={company}
                            onReload={reloadActivationAndLicenseData}
                        />
                    </TabPanel>

                    {license && (
                        <TabPanel
                            value={panel}
                            hidden={panel !== ActivationDetailsTab.Users}
                            index={ActivationDetailsTab.Users}
                        >
                            <VdsTable
                                ref={usersTableRef}
                                initialize={{
                                    sorting: {
                                        field: 'Email',
                                        order: 'Asc'
                                    },
                                }}
                                data={(query) => {

                                    return new Promise((result) => {

                                        const _filters: {
                                            name?: string;
                                            surname?: string;
                                            email?: string;
                                            pec?: string;
                                            isLegalRepresentative?: string;
                                            companyOid?: string;
                                        } = {};

                                        query.filters.forEach(({ field, filter }) => {
                                            _filters[field] = filter;
                                        });

                                        GetActivationUsers({
                                            params: {
                                                id: activation.id
                                            }
                                        }).then(({ data }) => {

                                            result({
                                                data: data?.users ?? [],
                                                totalCount: data?.users.length ?? 0
                                            });
                                        }).catch((error) => {

                                            setLoader(false);
                                            const errors = {}
                                            setAlert({
                                                message: errors[error?.response?.data?.title] || 'Si è verificato un errore imprevisto si prega di riprovare',
                                                severity: 'warning',
                                                open: true,
                                            });
                                        });
                                    })
                                }}
                                events={{
                                    onRowClick: (data) => {

                                        if (data.email !== profile.email && isActive) {

                                            setUserDialog(true);
                                            formik.setValues({
                                                roleOid: data.roleOid,
                                                email: data.email,
                                                edit: true,
                                                enabled: data.enabled,
                                                activationOid: data.activationOid
                                            });
                                            formik.setTouched({});
                                        }
                                    }
                                }}
                                actions={[
                                    {
                                        icon: <Refresh />,
                                        tooltip: "Aggiorna",
                                        onClick: () => {
                                            usersTableRef.current?.reload();
                                        }
                                    },
                                    (isActive && {
                                        icon: <Add />,
                                        tooltip: "Nuovo utente",
                                        onClick: (_ev) => {
                                            setUserDialog(true);
                                            formik.setValues({
                                                roleOid: '',
                                                email: '',
                                                edit: false,
                                                enabled: true,
                                                activationOid: ''
                                            });
                                            formik.setTouched({});
                                        }
                                    }),
                                ]}
                                options={{
                                    usePagination: false,
                                    useSearch: false,
                                    minWidth: "1100px",
                                    style: {
                                        maxHeight: "calc(100vh - 380px)"
                                    }
                                }}
                                columns={[
                                    {
                                        title: () => "Email",
                                        render: ({ email }) => email || '',
                                    },
                                    {
                                        title: () => "Nome",
                                        thStyle: { width: '100px' },
                                        render: ({ name }) => name || '',
                                    },
                                    {
                                        title: () => "Cognome",
                                        thStyle: { width: '100px' },
                                        render: ({ surname }) => surname || '',
                                    },
                                    {
                                        title: () => "Username",
                                        thStyle: { width: '120px' },
                                        render: ({ externalUsername }) => externalUsername || '',
                                    },
                                    {
                                        title: () => "Ruolo",
                                        style: { width: '150px', },
                                        thStyle: { width: '150px', },
                                        render: ({ roleName }) => {
                                            if (roleName === 'CompanyAdmin')
                                                return (
                                                    <Chip label="Amministratore" className={styles.chipAmministratore} />
                                                );
                                            else if (roleName === 'Operator')
                                                return (
                                                    <Chip label="Operatore" className={styles.chipOperator} />
                                                )
                                            else
                                                return "---"
                                        },
                                    },
                                    {
                                        title: () => "Data aggiunta",
                                        thStyle: { width: '120px' },
                                        render: ({ creationDate }) => {
                                            return creationDate ? moment(creationDate).format('DD/MM/YYYY, HH:mm') : '---';
                                        }
                                    },
                                    {
                                        title: () => "Ultimo accesso",
                                        thStyle: { width: '120px' },
                                        render: ({ lastLogin }) => {
                                            return lastLogin ? moment(lastLogin).format('DD/MM/YYYY, HH:mm') : '---';
                                        }
                                    },
                                    {
                                        title: () => "Abilitato",
                                        render: ({ enabled }) => renderBooleanIcon(enabled),
                                        thStyle: { width: 48 },
                                    },
                                    {
                                        tooltip: "Elimina",
                                        icon: <BiTrash style={{ fill: '#D82829' }} />,
                                        hidden: ({ email }) => !isActive || email === profile.email,
                                        style: { padding: '4px 5px', width: 25, textAlign: "center", svg: { color: '#D82829', fill: '#D82829' } },
                                        thStyle: { padding: '4px 5px', width: 25, textAlign: "center", svg: { color: '#D82829', fill: '#D82829' } },
                                        onClick: ({ email, activationOid }) => {

                                            setDialog({
                                                open: true,
                                                variant: 'error',
                                                title: `Conferma eliminazione`,
                                                content: `Sicuro di voler eliminare l'utente "${email}"?`,
                                                yesBtnTitle: 'Elimina',
                                                noBtnTitle: 'Annulla',
                                                noCallback: (closeDialog) => {

                                                    closeDialog();
                                                },
                                                yesCallback: (closeDialog) => {

                                                    setLoader(true);
                                                    DeleteActivationUser({
                                                        params: {
                                                            id: activationOid
                                                        }
                                                    }).then(() => {

                                                        setAlert({
                                                            open: true,
                                                            severity: 'success',
                                                            message: 'Utente eliminato con successo'
                                                        });
                                                        usersTableRef.current?.reload();
                                                        setLoader(false);
                                                        closeDialog();
                                                    }).catch((error) => {
                                                        setLoader(false);
                                                        const errors = {
                                                        }
                                                        setAlert({
                                                            message: errors[error?.response?.data?.title] || 'Si è verificato un errore imprevisto si prega di riprovare',
                                                            severity: 'warning',
                                                            open: true,
                                                        });
                                                    });
                                                }
                                            });
                                        },
                                    },
                                ]}
                            />
                        </TabPanel>
                    )}

                </Paper>
            </Container>

            <Dialog
                open={userDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{formik.values.edit ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
                <DialogContent>
                    <Grid
                        spacing={2}
                        container
                        component="form"
                        spellCheck="false"
                        autoComplete="off"
                    >
                        <Grid xs={12} item>
                            <VdsTextField
                                label="Email*"
                                name="email"
                                onChange={formik.handleChange}
                                disabled={formik.values.edit}
                                value={formik.values.email}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.touched.email && formik.errors.email)}
                                errorText={formik.touched.email && formik.errors.email}
                                collapse
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <VdsSelect
                                label="Ruolo*"
                                name="roleOid"
                                onChange={formik.handleChange}
                                value={formik.values.roleOid}
                                error={Boolean(formik.touched.roleOid && formik.errors.roleOid)}
                                errorText={formik.touched.roleOid && formik.errors.roleOid}
                                collapse
                            >
                                {roles.map(({ oid, name }) => {
                                    return (
                                        <MenuItem value={oid} key={`role-${oid}`}>
                                            {rolesTslMap[name]}
                                        </MenuItem>
                                    );
                                })}
                            </VdsSelect>
                        </Grid>
                        {formik.values.edit && (
                            <Grid xs={12} item>
                                <FormControlLabel
                                    label="Abilitato"
                                    control={(
                                        <Switch
                                            name="enabled"
                                            checked={formik.values.enabled}
                                            onChange={formik.handleChange}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setUserDialog(false);
                        }}
                    >
                        Annulla
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => formik.handleSubmit()}
                    >
                        Salva
                    </Button>
                </DialogActions>
            </Dialog>

        </StyledPageContainer>
    );
};
